import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const BASE_MS_PER_WORD = 280;

const LCVTutorDialog = ({
  text,
  label,
  animated = false,
  className,
  progressRatio,
  onFinished,
  fontSize: externalFontSize,
  showControls = true,
  paused = false,
  onFontSizeChange,
  onRestart,
  onPause,
  onResume,
  canPause = false,
  onSkip,
  canSkip = false,
  canReplay = false,
  onProgress,
}) => {
  const [internalFontSize, setInternalFontSize] = useState(17);
  const [wordIndex, setWordIndex] = useState(0);

  const fontSize = externalFontSize ?? internalFontSize;
  const handleFontSizeChange = onFontSizeChange ?? setInternalFontSize;
  const hasExternalProgress =
    typeof progressRatio === 'number' && Number.isFinite(progressRatio);
  const clampedProgress = hasExternalProgress
    ? Math.max(0, Math.min(1, progressRatio))
    : null;

  const words = useMemo(
    () => String(text || '').trim().split(/\s+/).filter(Boolean),
    [text],
  );
  const finishedRef = useRef(false);
  const containerRef = useRef(null);
  const activeWordRef = useRef(null);

  useEffect(() => {
    setWordIndex(0);
    finishedRef.current = false;
  }, [text]);

  useEffect(() => {
    onProgress?.({
      spokenWords: Math.min(
        hasExternalProgress
          ? Math.floor(clampedProgress * words.length)
          : wordIndex,
        words.length,
      ),
      totalWords: words.length,
    });
  }, [clampedProgress, hasExternalProgress, onProgress, wordIndex, words.length]);

  useEffect(() => {
    if (
      !animated ||
      paused ||
      words.length === 0 ||
      hasExternalProgress
    )
      return undefined;
    if (wordIndex >= words.length) {
      if (!finishedRef.current) {
        finishedRef.current = true;
        onFinished?.();
      }
      return undefined;
    }

    const timer = window.setTimeout(
      () => setWordIndex(current => current + 1),
      BASE_MS_PER_WORD,
    );

    return () => window.clearTimeout(timer);
  }, [animated, hasExternalProgress, onFinished, paused, wordIndex, words.length]);

  useEffect(() => {
    if (!animated || !hasExternalProgress) return undefined;
    if (clampedProgress >= 1) {
      if (!finishedRef.current) {
        finishedRef.current = true;
        onFinished?.();
      }
      return undefined;
    }

    finishedRef.current = false;
    return undefined;
  }, [animated, clampedProgress, hasExternalProgress, onFinished]);

  const spokenWordCount = useMemo(() => {
    if (!animated) return words.length;
    if (hasExternalProgress) {
      return Math.min(
        words.length,
        Math.max(
          0,
          clampedProgress > 0 ? Math.ceil(clampedProgress * words.length) : 0,
        ),
      );
    }

    return Math.min(wordIndex, words.length);
  }, [animated, clampedProgress, hasExternalProgress, wordIndex, words.length]);

  useEffect(() => {
    if (!activeWordRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const activeWord = activeWordRef.current;
    const wordBottom = activeWord.offsetTop + activeWord.offsetHeight;
    const visibleBottom = container.scrollTop + container.clientHeight;

    if (wordBottom > visibleBottom - 4) {
      container.scrollTop = wordBottom - container.clientHeight + 8;
    }
  }, [wordIndex]);

  const handleRestart = useCallback(() => {
    if (onRestart) {
      onRestart();
      return;
    }

    setWordIndex(0);
    finishedRef.current = false;
  }, [onRestart]);

  return (
    <div className={['lcv-tutor-dialog', className].filter(Boolean).join(' ')}>
      <div className="lcv-tutor-dialog-header">
        <div className="lcv-tutor-avatar-img" aria-hidden="true">
          🎩
        </div>
        <div className="lcv-tutor-name">Benjamin Franklin</div>
        {label && <span className="lcv-tutor-phase-label">{label}</span>}
      </div>

      <div
        ref={containerRef}
        className="lcv-tutor-dialog-text"
        style={{ fontSize: `${fontSize}px` }}
      >
        {animated
          ? words.map((word, index) => (
            <span
              key={`${word}-${index}`}
              ref={index === spokenWordCount - 1 ? activeWordRef : undefined}
              className={
                index < spokenWordCount ? 'lcv-tw-spoken' : 'lcv-tw-pending'
              }
            >
              {word}{' '}
            </span>
          ))
          : text}
      </div>

      {showControls && (
        <div className="lcv-tutor-toolbar">
          <div className="lcv-tutor-tool-group">
            <span className="lcv-tutor-tool-label">Text</span>
            <button
              type="button"
              className="lcv-tutor-tool-btn"
              disabled={fontSize <= 14}
              onClick={() => handleFontSizeChange(fontSize - 1)}
            >
              A-
            </button>
            <span className="lcv-tutor-tool-value">{fontSize}px</span>
            <button
              type="button"
              className="lcv-tutor-tool-btn"
              disabled={fontSize >= 24}
              onClick={() => handleFontSizeChange(fontSize + 1)}
            >
              A+
            </button>
          </div>

          {(canSkip || canPause || canReplay) && (
            <div className="lcv-tutor-tool-actions">
              {canReplay && (
                <button
                  type="button"
                  className="lcv-tutor-tool-toggle"
                  onClick={handleRestart}
                >
                  Play Again
                </button>
              )}

              {canSkip && (
                <button
                  type="button"
                  className="lcv-tutor-tool-toggle"
                  onClick={onSkip}
                >
                  Skip
                </button>
              )}

              {canPause && (
                <button
                  type="button"
                  className="lcv-tutor-tool-toggle"
                  onClick={paused ? onResume : onPause}
                >
                  {paused ? 'Continue' : 'Pause'}
                </button>
              )}
            </div>
          )}

          {/* {animated && (
            <button
              type="button"
              className="lcv-tutor-tool-restart"
              onClick={handleRestart}
            >
              Watch Again
            </button>
          )} */}
        </div>
      )}
    </div>
  );
};

export default LCVTutorDialog;
